import { getDefaults } from '@/App/Service/Setting/Registry';
import State from './State';

/**
 * Getters are used to calculate values based off state. They are generally
 * used for filtering or formatting data.
 *
 * @class Getters
 * @package Store/Module/Settings
 * @project ChalkySticks App
 */
const getters = {
	/**
	 * Get the API version from server config
	 *
	 * @param State state
	 * @return string|null
	 */
	apiVersion(state: typeof State): string | null {
		return state.apiVersion;
	},

	/**
	 * Get all app settings merged with defaults
	 *
	 * @param State state
	 * @return Record<string, any>
	 */
	appSettings(state: typeof State): Record<string, any> {
		return {
			...getDefaults(),
			...state.appSettings,
		};
	},

	/**
	 * If we should ask the user for location
	 *
	 * @param State state
	 * @return boolean
	 */
	canUseLocation(state: typeof State): boolean {
		return getters.onboardingComplete(state) && state.useLocation;
	},

	/**
	 * Get the unique device identifier
	 *
	 * @param State state
	 * @return string|null
	 */
	deviceId(state: typeof State): string | null {
		return state.deviceId;
	},

	/**
	 * Get all features as an object
	 *
	 * @param State state
	 * @return IFeatures
	 */
	features(state: typeof State): IFeatures {
		return state.features || {};
	},

	/**
	 * Get the feed structure configuration
	 *
	 * @param State state
	 * @return IFeedStructure[]
	 */
	feedStructure(state: typeof State): IFeedStructure[] {
		let output = state.useRemoteFeed ? state.feedStructure || [] : [];

		if (!output.length) {
			output = [
				{
					component: 'ViewFeedTVPlayingNow',
					enabled: true,
					props: {},
				},

				{
					component: 'ViewFeedUserNew',
					enabled: true,
					props: {},
				},
				{
					component: 'ViewFeedVenueNew',
					enabled: true,
					props: {},
				},
				{
					component: 'ViewFeedVenueNewNearby',
					enabled: true,
					props: {},
				},

				{
					component: 'ViewFeedVenueNearby',
					enabled: true,
					props: {},
				},
				{
					component: 'ViewFeedYouTubeDrDave',
					enabled: true,
					props: {},
				},
				{
					component: 'ViewFeedContentEventFlyer',
					enabled: true,
					props: {},
				},
				{
					component: 'ViewFeedUsersNearby',
					enabled: true,
					props: {},
				},
				{
					component: 'ViewFeedFact',
					enabled: true,
					props: {},
				},
				{
					authRequired: true,
					component: 'ViewFeedUserFavoriteVenues',
					enabled: true,
					props: {},
				},

				// mk: Makes the scroll wheel fail
				// {
				// 	component: 'ViewFeedWhatsYourOut',
				// 	enabled: true,
				// 	props: {},
				// },
				{
					component: 'ViewFeedVenueFeed',
					enabled: true,
					props: {},
				},
				{
					component: 'ViewFeedAdvertisement',
					enabled: true,
					props: {},
				},

				{
					component: 'ViewFeedVenueCheckinsNearby',
					enabled: true,
					props: {},
				},
				{
					component: 'ViewFeedYouTube',
					enabled: true,
					props: {
						tags: ['youtube'],
						youtubeName: `Stephen Hendry's Cue Tips`,
						youtubeUrl: 'https://www.youtube.com/@stephenhendryscuetips',
					},
				},

				{
					component: 'ViewFeedHallsNearby',
					enabled: true,
					props: {},
				},
				{
					component: 'ViewFeedVenueFeed',
					enabled: true,
					props: {},
				},
				{
					component: 'ViewFeedMessageToThePeople',
					enabled: true,
					props: {},
				},
			];
		}

		return output;
	},

	/**
	 * Check if location permission was denied
	 *
	 * @param State state
	 * @return boolean
	 */
	locationDenied(state: typeof State): boolean {
		return !!state.locationDenied;
	},

	/**
	 * If the user has completed the onboarding experience
	 *
	 * @param State state
	 * @return boolean
	 */
	onboardingComplete(state: typeof State): boolean {
		return state.onboardingComplete || false;
	},

	/**
	 * Get the saved push notification token
	 *
	 * @param State state
	 * @return string|null
	 */
	pushToken(state: typeof State): string | null {
		return state.pushToken;
	},

	/**
	 * Get the PWA install status (accepted/dismissed/null)
	 *
	 * @param State state
	 * @return 'accepted' | 'dismissed' | null
	 */
	pwaInstallStatus(state: typeof State): 'accepted' | 'dismissed' | null {
		return state.pwaInstallStatus || null;
	},

	/**
	 * Check if location services are enabled
	 *
	 * @param State state
	 * @return boolean
	 */
	useLocation(state: typeof State): boolean {
		return !!state.useLocation;
	},

	/**
	 * If we should use feed structure given to use by the API
	 *
	 * @param State state
	 * @return boolean
	 */
	useRemoteFeed(state: typeof State): boolean {
		return !!state.useRemoteFeed;
	},
};

export default getters;
